import { AxiosResponse } from 'axios';
import { FavoritePageFiltersType } from 'pages/FavoritesPage';
import qs from 'qs';
import { axiosApiInstance } from 'Store';
import { convertPrice } from 'utils/convertPrice';
import { formatFilterPrice } from 'utils/formatFilterPrice';
import { NormalizedPropertyType } from 'utils/formatters/getNormalizedPropertyType';
import { getNormalizedRentModuleName } from 'utils/formatters/getNormalizedRentModuleName';
import { getPropertyByTypeRent } from 'utils/formatters/getPropertyByType';
import { stringToNumber } from 'utils/formatters/stringToNumber';
import { RentOfferDetailsResponse, RentOfferDuplicatesResponse, RentOfferHashResponse, RentOfferResponseContent, RentOffersResponse } from 'utils/state-managment/rent/rentOffer';
import { SavedFilter } from 'utils/types/Filter';
import { RentFilterReturnData } from 'utils/types/Rent';
import { Module } from 'utils/types/RentModels';
import { OpinionUpdateInformation, RentOpinionResult } from 'utils/types/RentModels';
import { LocationItem } from 'utils/types/V2Locations';
import { v4 } from 'uuid';

import { activeNotification } from 'components/functions/activeNotification';

import { userApiRequests } from './user';

const addOpinion = (advertisementId: number, content: string, numberOfStars: number, module: Module): Promise<OpinionUpdateInformation> => {
    return axiosApiInstance({
        method: 'post',
        url: `/v2/rent-offer/opinion/create?module=${module}`,
        data: {
            advertisementId,
            content,
            numberOfStars
        }
    })
        .then((response) => {
            activeNotification('Udało się!', 'Opinia została dodana', 'success');

            return response.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification('Problem z dodaniem opinii', 'Niestety nie udało się dodać opinii', 'danger');
        });
};

const updateOpinion = (opinionId: number, content: string, numberOfStars: number, module: Module): Promise<OpinionUpdateInformation> => {
    return axiosApiInstance({
        method: 'put',
        url: `/v2/rent-offer/opinion/update?module=${module}`,
        data: {
            opinionId,
            content,
            numberOfStars
        }
    })
        .then((response) => {
            activeNotification('Udało się!', 'Opinia została za aktualizowana', 'success');

            return response.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification('Problem z dodaniem opinii', 'Niestety nie udało się aktualizować opinii', 'danger');
        });
};

const deleteOpinion = (opinionId: number, module: Module): Promise<OpinionUpdateInformation> => {
    return axiosApiInstance({
        method: 'delete',
        url: `/v2/rent-offer/opinion/delete?module=${module}&opinionId=${opinionId}`
    })
        .then((response) => {
            activeNotification('Udało się!', 'Opinia została usunięta', 'success');

            return response.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification('Problem z dodaniem opinii', 'Niestety nie udało się usunąć opinii', 'danger');
        });
};

const getOpinions = (advertisementId: number, module: Module): Promise<RentOpinionResult> => {
    return axiosApiInstance({
        method: 'get',
        url: `/v2/rent-offer/opinion/find?module=${module}&rentId=${advertisementId}`
    })
        .then((resp) => {
            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification('Problem z pobraniem opinii', 'Niestety nie udało się pobrać opinii', 'danger');
        });
};

const getRentOfferByHashId = async (hash: string): Promise<RentOfferHashResponse> => {
    return axiosApiInstance
        .get(`/v2/rent-offer/single-advertisement/detail?hash=${hash}`)
        .then((resp) => {
            if (resp.data) {
                return {
                    ...resp.data,
                    price: resp.data.price ? convertPrice(resp.data.price) : ''
                };
            }
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification(
                'Nie udało się pobrać ogłoszenia',
                'Nie udało się pobrać danych szczegółowych ogłoszenia',
                'warning'
            );

            return null;
        });
};

const getRentOffers = (params: RentFilterReturnData, propertyType: Module, page: number, size: number): Promise<RentOffersResponse> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { locations, filterLocations, ...paramsWithoutLocations } = params;

    const locationIds = Object.keys(params.savedLocations).map((id) => {
        return { id: Number(id) };
    });

    return axiosApiInstance({
        method: 'post',
        url: `/v2/rent-offer/advertisement/${getNormalizedRentModuleName(propertyType)}`,
        params: {
            page,
            size
        },
        data: {
            ...paramsWithoutLocations,
            filterLocations: locationIds
        }
    })
        .then((resp) => {
            if (resp.data) {
                return {
                    ...resp.data,
                    content: resp.data.content.map((el: RentOfferResponseContent) => ({
                        ...el,
                        price: el.price ? convertPrice(el.price) : '',
                        pricePerSquareMeter: el.pricePerSquareMeter ? convertPrice(el.pricePerSquareMeter) : '',
                        reaction: {
                            likeStatus: el.reaction?.likeStatus,
                            unlikeStatus: el.reaction?.unlikeStatus,
                            viewedStatus: el.reaction?.viewedStatus,
                            outdatedStatus: el.reaction?.outdatedStatus,
                            arrangedStatus: el.reaction?.arrangedStatus,
                            phoneStatus: el.reaction?.phoneStatus,
                            hideStatus: el.reaction?.hideStatus,
                            openedStatus: el.reaction?.openedStatus
                        }
                    }))
                };
            }

            return { empty: true, content: [], afterSearch: true };
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification(
                'Nie znaleziono wyników',
                'Wyszukiwanie nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );

            return { empty: true, content: [], afterSearch: true };
        });
};

const getRentOfferDuplicates = async (propertyType: Module, id: number): Promise<RentOfferDuplicatesResponse[]> => {
    return axiosApiInstance
        .get(`/v2/rent-offer/duplicate/find/${getNormalizedRentModuleName(propertyType)}?advertisementId=${id}`)
        .then((resp) => {
            if (resp.data) {
                return resp.data;
            }
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification(
                'Nie udało się pobrać duplikatów ogłoszenia',
                'Nie udało się pobrać danych szczegółowych duplikatów ogłoszenia',
                'warning'
            );

            return [];
        });
};

const getRentOfferDetails = async (propertyType: Module, id: number): Promise<RentOfferDetailsResponse> => {
    return axiosApiInstance
        .get(`/v2/rent-offer/advertisement/detail/${getNormalizedRentModuleName(propertyType)}?advertisementId=${id}`)
        .then((resp) => {
            if (resp.data) {
                return {
                    ...resp.data,
                    price: resp.data.price ? convertPrice(resp.data.price) : ''
                };
            }
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification(
                'Nie udało się pobrać ogłoszenia',
                'Nie udało się pobrać danych szczegółowych ogłoszenia',
                'warning'
            );

            return [{ empty: true }];
        });
};

const getOffersCSVFile = (params: RentFilterReturnData, propertyType: NormalizedPropertyType) => {
    return axiosApiInstance({
        method: 'post',
        responseType: 'blob',
        url: `/v2/rent-offer/excel/${propertyType}`,
        data: {
            ...params,
            offerAdded: { from: params.offerAddedFrom, to: params.offerAddedTo },
            priceFrom: stringToNumber(params.priceFrom),
            priceTo: stringToNumber(params.priceTo),
            areaFrom: stringToNumber(params.areaFrom),
            areaTo: stringToNumber(params.areaTo),
            pricePerSquareMeterFrom: stringToNumber(params.pricePerSquareMeterFrom),
            pricePerSquareMeterTo: stringToNumber(params.pricePerSquareMeterTo),
            rentPriceFrom: stringToNumber(params.rentPriceFrom),
            rentPriceTo: stringToNumber(params.rentPriceTo),
            depositPriceFrom: stringToNumber(params.depositPriceFrom),
            depositPriceTo: stringToNumber(params.depositPriceTo),
            filterLocations: Object.keys(params.savedLocations).map((id) => {
                return { id: Number(id) };
            })
        }
    })
        .then((resp) => {
            const url = URL.createObjectURL(new Blob([resp.data]));
            const link = document.createElement('a');

            link.setAttribute('href', url);
            link.setAttribute('download', 'offers.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        });
};

const getSimilarOffersFilter = async (advertisementId: number, module: Module): Promise<RentFilterReturnData | null> => {
    return axiosApiInstance
        .get(`/v2/rent-offer/filter/similar/generate?advertisementId=${advertisementId}&module=${module}`)
        .then((resp) => {
            const filters = resp.data;
            const savedLocations = Array.isArray(filters.filterLocations)
                ? Object.fromEntries(
                    filters.filterLocations.map((location: LocationItem) => [
                        location.id,
                        // @ts-expect-error z Api dostajemy fullName
                        { ...location, label: location.fullName }
                    ])
                )
                : {};

            return {
                ...filters,
                wantedKeywords: filters.wantedKeywords ? filters.wantedKeywords : [],
                unwantedKeywords: filters.unwantedKeywords ? filters.unwantedKeywords : [],
                wantedPhoneNumbers: filters.wantedPhoneNumbers ? filters.wantedPhoneNumbers : [],
                unwantedPhoneNumbers: filters.unwantedPhoneNumbers ? filters.unwantedPhoneNumbers : [],
                buildingType: filters.buildingType ? filters.buildingType : [],
                typeOfMarket: filters.typeOfMarket ? filters.typeOfMarket : [],
                propertyType: filters.propertyType ? filters.propertyType : [],
                savedLocations,
                locations: [],
                filterLocations: [],
                searchLocationResult: []
            };
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification('Problem z pobraniem filtra', 'Nie udało się pobrać filtra dla podobnych ogłoszeń', 'danger');

            return null;
        });
};

const getSavedFilters = async (): Promise<SavedFilter[]> => {
    return axiosApiInstance
        .get<SavedFilter[]>('/v2/rent-offer/filter/list')
        .then((resp) => {
            if (resp.data) {
                return resp.data.map((el) => ({
                    ...el,
                    name: el.name
                }));
            }

            return [];
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);

            if (err?.response?.status === 401) {
                window.parent.location.reload();
            }

            return [];
        });
};

const getFilterDetailsRent = async (filterId: number, filterPropertyType: Module): Promise<RentFilterReturnData> => {
    return axiosApiInstance
        .get(`/v2/rent-offer/filter/detail/${getNormalizedRentModuleName(filterPropertyType)}?id=${filterId}`)
        .then((resp) => {
            const filters = resp.data;
            const savedLocations = Array.isArray(filters.filterLocations)
                ? Object.fromEntries(
                    filters.filterLocations.map((location: LocationItem) => [
                        location.id,
                        // @ts-expect-error z Api dostajemy fullName
                        { ...location, label: location.fullName }
                    ])
                )
                : {};

            return {
                ...filters,
                wantedKeywords: filters.wantedKeywords ? filters.wantedKeywords : [],
                unwantedKeywords: filters.unwantedKeywords ? filters.unwantedKeywords : [],
                wantedPhoneNumbers: filters.wantedPhoneNumbers ? filters.wantedPhoneNumbers : [],
                unwantedPhoneNumbers: filters.unwantedPhoneNumbers ? filters.unwantedPhoneNumbers : [],
                buildingType: filters.buildingType ? filters.buildingType : [],
                typeOfMarket: filters.typeOfMarket ? filters.typeOfMarket : [],
                propertyType: filters.propertyType ? filters.propertyType : [],
                id: filterId,
                name: filters.name ? filters.name : '',
                pushAlert: filters.pushAlert ? filters.pushAlert : false,
                alertEmail: filters.alertEmail ? filters.alertEmail : false,
                alertSms: filters.alertSms ? filters.alertSms : false,
                priceFrom: filters.priceFrom ? formatFilterPrice(filters.priceFrom) : '',
                priceTo: filters.priceTo ? formatFilterPrice(filters.priceTo) : '',
                depositPriceFrom: filters.depositPriceFrom ? formatFilterPrice(filters.depositPriceFrom) : '',
                depositPriceTo: filters.depositPriceTo ? formatFilterPrice(filters.depositPriceTo) : '',
                areaFrom: filters.areaFrom ? filters.areaFrom : '',
                areaTo: filters.areaTo ? filters.areaTo : '',
                pricePerSquareMeterFrom: filters.pricePerSquareMeterFrom ? formatFilterPrice(filters.pricePerSquareMeterFrom) : '',
                pricePerSquareMeterTo: filters.pricePerSquareMeterTo ? formatFilterPrice(filters.pricePerSquareMeterTo) : '',
                numberOfRoomsFrom: filters.numberOfRoomsFrom ? filters.numberOfRoomsFrom : '',
                numberOfRoomsTo: filters.numberOfRoomsTo ? filters.numberOfRoomsTo : '',
                floorFrom: filters.floorFrom ? filters.floorFrom : '',
                floorTo: filters.floorTo ? filters.floorTo : '',
                builtYearFrom: filters.builtYearFrom ? filters.builtYearFrom : '',
                builtYearTo: filters.builtYearTo ? filters.builtYearTo : '',
                module: filters.module ? filters.module : '',
                tossedUp: filters.tossedUp ? filters.tossedUp : false,
                notificationsDelay: filters.notificationsDelay !== null && filters.notificationsDelay !== undefined
                    ? filters.notificationsDelay.toString()
                    : '0',
                groupDuplicate: filters.groupDuplicate ? filters.groupDuplicate : false,
                includeZeroArea: filters.includeZeroArea ? filters.includeZeroArea : false,
                modification: filters.modification ? true : filters.modification,
                offerAddedFrom: filters.offerAddedFrom ? filters.offerAddedFrom : '',
                offerAddedTo: filters.offerAddedTo ? filters.offerAddedTo : '',
                savedFilter: filters.savedFilter ? filters.savedFilter : false,
                locations: [],
                filterLocations: [],
                savedLocations: savedLocations,
                searchLocationResult: []
            };
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification('Problem z załadowaniem danych', 'Niestety nie udało się załadować danych. Spróbuj ponownie później', 'danger');
        });
};

const removeSavedFilter = (filterId: number, adType: Module) => {
    return axiosApiInstance
        .delete(`/v2/rent-offer/filter/delete?id=${filterId}&module=${getPropertyByTypeRent(adType)}`)
        .then(() => {
            activeNotification('Udało się!', 'Filtr został usunięty', 'success');
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification('Problem z usunięciem', 'Niestety nie udało się usunąć danych. Spróbuj ponownie później', 'danger');
        });
};

const addFilter = (params: RentFilterReturnData, propertyType: NormalizedPropertyType): Promise<AxiosResponse<number>> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { locations, filterLocations, ...paramsWithoutLocations } = params;
    const locationIds = Object.keys(params.savedLocations).map((id) => {
        return { id: Number(id) };
    });

    return axiosApiInstance({
        method: 'post',
        url: `/v2/rent-offer/filter/add/${propertyType}`,
        data: {
            ...paramsWithoutLocations,
            filterLocations: locationIds,
            notificationsDelay: params.notificationsDelay ? stringToNumber(params.notificationsDelay) : 0
        },
        paramsSerializer: function (params) {
            return qs.stringify(params);
        }
    })
        .then((resp) => {
            activeNotification('Udało się!', 'Filtr został dodany', 'success');

            return resp;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);

            if (err?.response?.status === 400) {
                activeNotification(
                    'Błąd',
                    err.response.data,
                    'danger'
                );

                return Promise.reject(err);
            } else {
                const errorId = v4();
                userApiRequests.logException(err, errorId);
                activeNotification(
                    'Coś poszło nie tak!',
                    'Spróbuj ponownie',
                    'danger'
                );

                return Promise.reject(err);
            }
        });
};

const updateFilter = async (params: RentFilterReturnData, propertyType: string) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { locations, filterLocations, ...paramsWithoutLocations } = params;
    const locationIds = Object.keys(params.savedLocations).map((id) => {
        return { id: Number(id) };
    });

    return axiosApiInstance({
        method: 'put',
        url: `/v2/rent-offer/filter/edit/${propertyType}`,
        params: { id: params.id },
        data: {
            ...paramsWithoutLocations,
            filterLocations: locationIds,
            notificationsDelay: params.notificationsDelay ? stringToNumber(params.notificationsDelay) : 0
        },
        paramsSerializer: function (paramsWithoutLocations) {
            return qs.stringify(paramsWithoutLocations, { arrayFormat: 'repeat' });
        }
    })
        .then((resp) => {
            activeNotification('Udało się!', 'Filtr został zaktualizowany', 'success');

            return resp;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId);
            activeNotification('Problem z zapisem', 'Niestety nie udało się zapisać danych. Spróbuj ponownie później', 'danger');

            return Promise.reject(err);
        });
};

const getRentFavoriteOffers = (modules: string[], page?: number, size?: number, filters?: FavoritePageFiltersType, sortType?: string): Promise<RentOffersResponse> => {
    return axiosApiInstance({
        method: 'get',
        url: '/favorite/find/rent',
        params: {
            module: modules,
            ...filters,
            ...sortType && { sort: sortType },
            page,
            size
        },
        paramsSerializer: (params) => {
            return qs.stringify(params, { arrayFormat: 'repeat' });
        }
    })
        .then((resp) => {
            if (resp.data.content && resp.data.content.length > 0) {
                return {
                    ...resp.data,
                    pageable: {
                        ...resp.data.pageable,
                        pageNumber: resp.data.pageable.pageNumber + 1
                    }
                };
            }

            return { empty: true, content: [], afterSearch: true };
        })
        .catch(() => {
            activeNotification(
                'Nie znaleziono wyników',
                'Obecnie w naszej bazie nie posiadamy ogłoszeń, które spełniają twoje kryteria. Spróbuj zmienić kryteria i wyszukaj jeszcze raz.',
                'warning'
            );

            return { empty: true, content: [], afterSearch: true };
        });
};

export const rentOffersApiRequests = {
    addOpinion,
    updateOpinion,
    deleteOpinion,
    getOpinions,
    getRentOfferByHashId,
    getRentOffers,
    getRentOfferDuplicates,
    getRentOfferDetails,
    getOffersCSVFile,
    getRentFavoriteOffers,
    getFilterDetailsRent,
    getSimilarOffersFilter,
    getSavedFilters,
    removeSavedFilter,
    addFilter,
    updateFilter
};
